import React, { createContext, useState, useEffect, useContext } from 'react';
import { publicApi } from '../utils/publicApi';

const AuthContext = createContext(null);

// LocalStorage keys
const STORAGE_KEY_AUTH = '@advantage_oman_admin';
const STORAGE_KEY_EMAIL = '@advantage_oman_admin_email';
const STORAGE_KEY_USER = '@advantage_oman_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check stored auth state on mount
  useEffect(() => {
    try {
      const isAdmin = localStorage.getItem(STORAGE_KEY_AUTH) === 'true';
      const email = localStorage.getItem(STORAGE_KEY_EMAIL) || '';
      const storedUser = localStorage.getItem(STORAGE_KEY_USER);
      const parsedUser = storedUser ? JSON.parse(storedUser) : null;

      if (isAdmin) {
        setUser(parsedUser || (email ? { email } : { email: '' }));
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error reading auth state from localStorage:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const trimmedEmail = (email || '').trim();
    const trimmedPassword = (password || '').trim();

    if (!trimmedEmail || !trimmedPassword) {
      throw new Error('Please enter both email and password.');
    }

    try {
      const response = await publicApi.login({
        email: trimmedEmail,
        password: trimmedPassword,
      });
      const userObj = response?.user || { email: trimmedEmail };

      localStorage.setItem(STORAGE_KEY_AUTH, 'true');
      localStorage.setItem(STORAGE_KEY_EMAIL, userObj.email || trimmedEmail);
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(userObj));
      setUser(userObj);
      setIsAuthenticated(true);

      return { success: true, user: userObj };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error?.message || '';
      if (errorMessage) {
        throw new Error(errorMessage);
      }
      throw new Error('Login failed. Please try again.');
    }
  };

  const refreshUser = async () => {
    if (!isAuthenticated) return null;
    try {
      const profile = await publicApi.getProfile();
      const currentRaw = localStorage.getItem(STORAGE_KEY_USER);
      const currentUser = currentRaw ? JSON.parse(currentRaw) : {};
      const nextUser = {
        ...currentUser,
        ...profile,
        profile_completed: Boolean(profile?.profile_completed),
        rsvp_confirmed: Boolean(profile?.rsvp_confirmed),
      };
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(nextUser));
      setUser(nextUser);
      return nextUser;
    } catch (_error) {
      return null;
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(STORAGE_KEY_AUTH);
      localStorage.removeItem(STORAGE_KEY_EMAIL);
      localStorage.removeItem(STORAGE_KEY_USER);
    } catch (error) {
      console.error('Error clearing auth state:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    refreshUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

